import { CsvEnum, Id, IsoDate, IsoDateTime, ListQuery, Paginated, QueryBool, QueryVersion, defineAction, defineEvent, updateFields, z } from '@mkcore/shared';
import { knowledgePermissions as P } from './permissions';

/**
 * Schemas of the knowledge layer (docs/04-domeinen/knowledge.md, docs/01-architectuur/09-kennislaag.md): spaces and
 * knowledge items with their versions, step 1 of U7. One schema per action gives the route, the MCP tool and the
 * types of the web app.
 */
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');

export const SPACE_KINDS = ['tenant', 'organisation', 'project', 'agent', 'shared'] as const;
export const GROUNDING = ['strict', 'open'] as const;
export const MEMBER_LEVELS = ['reader', 'writer'] as const;
export const ITEM_TYPES = ['note', 'decision', 'memory', 'handover', 'session', 'capture', 'procedure', 'policy'] as const;
export const ITEM_STATUSES = ['active', 'archived'] as const;
export const AUTHOR_KINDS = ['human', 'agent', 'system'] as const;
export const VERSION_REASONS = ['create', 'update', 'edit', 'move', 'archive', 'restore', 'revert', 'tags', 'links', 'import', 'supersede'] as const;
/** Where a capture lands in its space (CO-1094); an agent may also write here in spaces it only reads. */
export const INBOX = 'inbox';

// ---------- spaces (CO-1106) ----------

export const KnowledgeSpace = z
  .object({
    id: Id,
    kind: z.enum(SPACE_KINDS),
    name: z.string(),
    slug: z.string().describe('Unique per tenant; the folder of the space in the snapshot'),
    organisationId: Id.nullable(),
    projectId: Id.nullable(),
    agentId: Id.nullable().describe('For kind agent: the principal of the agent'),
    instructions: z.string().describe('Markdown that ask and the brief send along (CO-1155)'),
    localOnly: z.boolean().describe('No content to an external model'),
    grounding: z.enum(GROUNDING).describe('strict: answers only from sources (CO-1156)'),
    reviewAgentWrites: z.boolean().describe('A change by an agent waits for an approval (CO-0937)'),
    archivedAt: IsoDateTime.nullable(),
    itemCount: z.number().int().describe('Active items'),
    access: z.enum(['read', 'write']).describe('What the caller may do in the space'),
    version: z.number().int(),
  })
  .openapi('KnowledgeSpace');

const spaceFields = {
  name: z.string().trim().min(1).max(200),
  instructions: z.string().max(20_000).optional(),
  localOnly: z.boolean().optional(),
  grounding: z.enum(GROUNDING).optional(),
  reviewAgentWrites: z.boolean().optional(),
};

export const listSpaces = defineAction({
  name: 'knowledge.space.list',
  method: 'get',
  path: '/knowledge/spaces',
  permission: P.keys.read,
  input: z.object({
    kind: CsvEnum(SPACE_KINDS).optional(),
    organisationId: Id.optional(),
    projectId: Id.optional(),
    archived: QueryBool.optional().describe('Also archived spaces'),
  }),
  output: z.object({ items: z.array(KnowledgeSpace) }),
  summary: 'The knowledge spaces you may see, with what you may do in each (CO-1106)',
});

export const getSpace = defineAction({
  name: 'knowledge.space.get',
  method: 'get',
  path: '/knowledge/spaces/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: KnowledgeSpace,
  etag: true,
  summary: 'One knowledge space',
});

export const createSpace = defineAction({
  name: 'knowledge.space.create',
  method: 'post',
  path: '/knowledge/spaces',
  permission: P.keys.manage,
  input: z
    .object({
      kind: z.enum(SPACE_KINDS),
      organisationId: Id.optional().describe('For kind organisation'),
      projectId: Id.optional().describe('For kind project'),
      agentId: Id.optional().describe('For kind agent: the principal of the agent'),
      slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9][a-z0-9-]{0,62}$/)
        .optional()
        .describe('Default from the name'),
      ...spaceFields,
    })
    .strict(),
  output: KnowledgeSpace,
  summary: 'Make a knowledge space for the company, a client, a project, an agent or a shared collection',
});

export const updateSpace = defineAction({
  name: 'knowledge.space.update',
  method: 'patch',
  path: '/knowledge/spaces/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, ...updateFields(spaceFields), expectedVersion: ExpectedVersion }).strict(),
  output: KnowledgeSpace,
  etag: true,
  summary: 'Change the name, instructions or settings of a space',
});

export const archiveSpace = defineAction({
  name: 'knowledge.space.archive',
  method: 'post',
  path: '/knowledge/spaces/{id}/archive',
  permission: P.keys.manage,
  input: z.object({ id: Id, archived: z.boolean().default(true) }).strict(),
  output: KnowledgeSpace,
  successStatus: 200,
  summary: 'Archive a space (or bring it back): its items stay, but it is no longer offered for writing',
});

export const SpaceMember = z.object({ principalId: Id, name: z.string(), kind: z.enum(['human', 'agent', 'client']), level: z.enum(MEMBER_LEVELS) }).openapi('KnowledgeSpaceMember');

export const listMembers = defineAction({
  name: 'knowledge.space.members',
  method: 'get',
  path: '/knowledge/spaces/{id}/members',
  permission: P.keys.manage,
  input: z.object({ id: Id }),
  output: z.object({ items: z.array(SpaceMember) }),
  summary: 'Who reads or writes in a shared space, and what agents may do in a space',
});

export const grantMember = defineAction({
  name: 'knowledge.space.grant',
  method: 'post',
  path: '/knowledge/spaces/{id}/members',
  permission: P.keys.manage,
  input: z.object({ id: Id, principalId: Id.describe('A person or an agent (admin.user.list)'), level: z.enum(MEMBER_LEVELS) }).strict(),
  output: z.object({ items: z.array(SpaceMember) }),
  successStatus: 200,
  summary: 'Give a person or an agent read or write access to a space',
});

export const revokeMember = defineAction({
  name: 'knowledge.space.revoke',
  method: 'delete',
  path: '/knowledge/spaces/{id}/members/{principalId}',
  permission: P.keys.manage,
  input: z.object({ id: Id, principalId: Id }),
  output: z.object({ items: z.array(SpaceMember) }),
  successStatus: 200,
  summary: 'Take the access of a person or agent to a space away',
});

// ---------- items (CO-1111, CO-1112, CO-1103, CO-1149, CO-0499, CO-0501) ----------

export const KnowledgeItem = z
  .object({
    id: Id.describe('The permalink: it stays when the item is renamed or moved'),
    spaceId: Id,
    space: z.object({ id: Id, name: z.string(), kind: z.enum(SPACE_KINDS) }),
    path: z.string().describe('The folder in the space, such as processen/facturatie; empty is the root'),
    slug: z.string(),
    type: z.enum(ITEM_TYPES),
    title: z.string(),
    body: z.string().describe('Markdown without frontmatter'),
    fields: z.record(z.string(), z.unknown()).describe('Typed fields of the type, such as the motivation of a decision'),
    tags: z.array(z.string()),
    ownerId: Id.nullable(),
    sources: z.array(z.string()).describe('Where it comes from: meeting:<id>, ticket:<id>, a URL; document:<id>, item:<id> and question:<id> are kept as provenance (CO-1093)'),
    eventDate: IsoDate.nullable(),
    validUntil: IsoDateTime.nullable().describe('Superseded from this moment (CO-1157)'),
    supersededBy: Id.nullable().describe('The item that replaced it'),
    status: z.enum(ITEM_STATUSES),
    lockedForAgents: z.boolean().describe('Agents may read but not change it (CO-1106)'),
    contentHash: z.string().describe('Give it to knowledge.item.edit, so a change by someone else is not overwritten'),
    createdByKind: z.enum(AUTHOR_KINDS),
    origin: z.string().nullable().describe('Imported from, such as memory-mcp'),
    externalId: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('KnowledgeItem');

/** The item without its text, for lists. */
export const KnowledgeItemSummary = KnowledgeItem.omit({ body: true, fields: true }).extend({ excerpt: z.string() }).openapi('KnowledgeItemSummary');

/** Where an item goes: a space by id, or the space of a client or project (made when it is missing); default the company space, for an agent its own. */
const target = {
  spaceId: Id.optional().describe('The space (knowledge.space.list)'),
  organisationId: Id.optional().describe('Or the space of this client'),
  projectId: Id.optional().describe('Or the space of this project'),
};

const Path = z
  .string()
  .trim()
  .max(400)
  .describe('Folder, segments separated by /, such as processen/facturatie');
const Tags = z.array(z.string().trim().min(1).max(60)).max(30);
const Sources = z.array(z.string().trim().min(1).max(500)).max(50);

const itemFields = {
  title: z.string().trim().min(1).max(300),
  body: z.string().max(500_000).default(''),
  type: z.enum(ITEM_TYPES).optional().describe('Default note'),
  path: Path.optional(),
  fields: z.record(z.string(), z.unknown()).optional(),
  tags: Tags.optional(),
  sources: Sources.optional(),
  eventDate: IsoDate.nullable().optional(),
  lockedForAgents: z.boolean().optional(),
};

export const listItems = defineAction({
  name: 'knowledge.item.list',
  method: 'get',
  path: '/knowledge/items',
  permission: P.keys.read,
  input: ListQuery.extend({
    spaceId: Id.optional(),
    path: z.string().max(400).optional().describe('Only this folder'),
    recursive: QueryBool.optional().describe('With the folders below path'),
    type: CsvEnum(ITEM_TYPES).optional(),
    tag: z.string().max(60).optional(),
    status: CsvEnum(ITEM_STATUSES).optional().describe('Default active'),
    title: z.string().max(300).optional().describe('Words in the title, or the exact title'),
    origin: z.string().max(60).optional(),
    externalId: z.string().max(300).optional(),
    sort: z.enum(['updated', 'title', 'eventDate']).optional().describe('Default updated, newest first'),
  }),
  output: Paginated(KnowledgeItemSummary),
  summary: 'Browse knowledge items per space and folder, with filters on type, tag, status and title (CO-1095, CO-1112)',
});

export const getItem = defineAction({
  name: 'knowledge.item.get',
  method: 'get',
  path: '/knowledge/items/{id}',
  permission: P.keys.read,
  input: z.object({
    id: Id,
    format: z.enum(['json', 'markdown']).optional().describe('markdown: the item as one file with frontmatter'),
    from: z.coerce.number().int().min(0).optional().describe('First character of the text to return, for long items'),
    to: z.coerce.number().int().min(1).optional().describe('Character after the last one'),
  }),
  output: KnowledgeItem.extend({ markdown: z.string().nullable(), length: z.number().int().describe('Characters in the whole text') }),
  etag: true,
  summary: 'One knowledge item with its text, as fields or as markdown with frontmatter (CO-1095, CO-1111)',
});

export const createItem = defineAction({
  name: 'knowledge.item.create',
  method: 'post',
  path: '/knowledge/items',
  permission: P.keys.create,
  input: z
    .object({
      ...target,
      ...itemFields,
      origin: z.string().trim().max(60).optional().describe('Imported from, such as memory-mcp (CO-1113)'),
      externalId: z.string().trim().max(300).optional().describe('Id in the origin: the same one updates instead of making a second item'),
      createdAt: IsoDateTime.optional().describe('Only with origin: the original date'),
      supersedes: Id.optional().describe('An item this one replaces: it stays, valid until now (CO-1157)'),
    })
    .strict(),
  output: KnowledgeItem.extend({
    outcome: z.enum(['created', 'updated', 'unchanged']),
    similar: z
      .array(z.object({ id: Id, title: z.string(), similarity: z.number() }))
      .optional()
      .describe('Active items in the same space that look alike: update or merge one of them instead of keeping both (CO-1151)'),
  }),
  summary: 'Write a knowledge item: a note, decision, procedure or other type, in a space and folder (CO-1104, CO-1111)',
});

export const updateItem = defineAction({
  name: 'knowledge.item.update',
  method: 'patch',
  path: '/knowledge/items/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id, ...updateFields(itemFields), expectedVersion: ExpectedVersion }).strict(),
  output: KnowledgeItem,
  etag: true,
  summary: 'Change the title, text, type, fields or tags of an item; the old text stays as a version (CO-1103)',
});

export const editItem = defineAction({
  name: 'knowledge.item.edit',
  method: 'post',
  path: '/knowledge/items/{id}/edit',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      contentHash: z.string().min(8).max(100).describe('contentHash of the item you read'),
      op: z.enum(['append', 'replace', 'section']).describe('append: text at the end; replace: find becomes text; section: the text under heading'),
      text: z.string().max(200_000),
      find: z.string().min(1).max(20_000).optional().describe('For replace: the exact text to replace'),
      all: z.boolean().optional().describe('For replace: every occurrence; else find must occur once'),
      heading: z.string().trim().min(1).max(300).optional().describe('For section: the heading, such as Motivatie or ## Motivatie; a new section when it is missing'),
    })
    .strict(),
  output: KnowledgeItem,
  successStatus: 200,
  summary: 'Change part of an item without overwriting the change of someone else: append, replace text or a section (CO-1149)',
});

export const moveItem = defineAction({
  name: 'knowledge.item.move',
  method: 'post',
  path: '/knowledge/items/{id}/move',
  permission: P.keys.update,
  input: z.object({ id: Id, spaceId: Id.optional(), path: Path.optional(), title: z.string().trim().min(1).max(300).optional().describe('A new title; [[links]] to the old title follow') }).strict(),
  output: KnowledgeItem,
  successStatus: 200,
  summary: 'Move an item to another folder or space, or rename it; its id and links stay (CO-1103, CO-1149)',
});

export const archiveItem = defineAction({
  name: 'knowledge.item.archive',
  method: 'post',
  path: '/knowledge/items/{id}/archive',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: KnowledgeItem,
  successStatus: 200,
  summary: 'Archive an item: it stays, but search no longer finds it (CO-1103)',
});

export const restoreItem = defineAction({
  name: 'knowledge.item.restore',
  method: 'post',
  path: '/knowledge/items/{id}/restore',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: KnowledgeItem,
  successStatus: 200,
  summary: 'Bring an archived item back (CO-1103)',
});

export const bulkItems = defineAction({
  name: 'knowledge.item.bulk',
  method: 'post',
  path: '/knowledge/items/bulk',
  permission: P.keys.update,
  input: z
    .object({
      ids: z.array(Id).min(1).max(200),
      action: z.enum(['archive', 'restore', 'move', 'addTags', 'removeTags']),
      path: Path.optional().describe('For move'),
      tags: Tags.optional().describe('For addTags and removeTags'),
    })
    .strict(),
  output: z.object({ done: z.array(Id), failed: z.array(z.object({ id: Id, error: z.string() })) }),
  successStatus: 200,
  summary: 'Archive, restore, move or tag up to 200 items at once (CO-1103)',
});

export const ItemVersion = z
  .object({
    number: z.number().int(),
    reason: z.enum(VERSION_REASONS),
    title: z.string(),
    path: z.string(),
    authorId: Id,
    authorName: z.string(),
    authorKind: z.enum(AUTHOR_KINDS),
    createdAt: IsoDateTime,
  })
  .openapi('KnowledgeItemVersion');

export const listVersions = defineAction({
  name: 'knowledge.item.versions',
  method: 'get',
  path: '/knowledge/items/{id}/versions',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({ items: z.array(ItemVersion) }),
  summary: 'The history of an item, newest first: who changed it, when and how (CO-0501)',
});

export const getVersion = defineAction({
  name: 'knowledge.item.version',
  method: 'get',
  path: '/knowledge/items/{id}/versions/{number}',
  permission: P.keys.read,
  input: z.object({ id: Id, number: z.coerce.number().int().min(1) }),
  output: ItemVersion.extend({
    body: z.string(),
    fields: z.record(z.string(), z.unknown()),
    tags: z.array(z.string()),
    diff: z.string().describe('Unified diff of the text against the version before it'),
  }),
  summary: 'One version of an item with its text and the diff against the version before (CO-0501)',
});

export const revertItem = defineAction({
  name: 'knowledge.item.revert',
  method: 'post',
  path: '/knowledge/items/{id}/revert',
  permission: P.keys.update,
  input: z.object({ id: Id, number: z.number().int().min(1).describe('The version to go back to'), expectedVersion: ExpectedVersion }).strict(),
  output: KnowledgeItem,
  successStatus: 200,
  summary: 'Go back to an earlier version; that becomes a new version, so nothing is lost (CO-0501)',
});

export const rememberItem = defineAction({
  name: 'knowledge.item.remember',
  method: 'post',
  path: '/knowledge/remember',
  permission: P.keys.create,
  input: z
    .object({
      ...target,
      text: z.string().trim().min(1).max(20_000).describe('The fact, preference or decision, in a sentence or a few'),
      title: z.string().trim().max(300).optional().describe('Default the first sentence'),
      type: z.enum(['memory', 'decision', 'note', 'procedure']).optional().describe('Default memory'),
      tags: Tags.optional(),
      sources: Sources.optional(),
      eventDate: IsoDate.optional(),
      supersedes: Id.optional().describe('A memory this one replaces, such as an old preference: it stays, valid until now (CO-1157)'),
    })
    .strict(),
  output: KnowledgeItem.extend({ outcome: z.enum(['created', 'updated', 'skipped']).describe('skipped: it was known already') }),
  successStatus: 200,
  summary: 'Remember something: a new memory, or the one that says nearly the same updated, or nothing when it is known (CO-1108, CO-1151)',
});

export const captureItem = defineAction({
  name: 'knowledge.item.capture',
  method: 'post',
  path: '/knowledge/capture',
  permission: P.keys.create,
  input: z
    .object({
      ...target,
      text: z.string().min(1).max(200_000).describe('Literally, as it is: an answer, an outcome, a note'),
      title: z.string().trim().max(300).optional(),
      tags: Tags.optional(),
      sources: Sources.optional(),
    })
    .strict(),
  output: KnowledgeItem,
  summary: 'Capture text literally in the inbox of a space, to process or publish later (CO-1094)',
});

// ---------- folders and tags (CO-0499, CO-1112) ----------

export const listFolders = defineAction({
  name: 'knowledge.folder.list',
  method: 'get',
  path: '/knowledge/folders',
  permission: P.keys.read,
  input: z.object({ spaceId: Id, under: z.string().max(400).optional().describe('Only below this folder'), depth: z.coerce.number().int().min(1).max(10).optional().describe('Default all') }),
  output: z.object({ items: z.array(z.object({ path: z.string(), items: z.number().int().describe('Active items and documents directly in it'), total: z.number().int().describe('With the folders below') })) }),
  summary: 'The folders of a space with their number of items (CO-0499, CO-1095)',
});

export const moveFolder = defineAction({
  name: 'knowledge.folder.move',
  method: 'post',
  path: '/knowledge/folders/move',
  permission: P.keys.update,
  input: z.object({ spaceId: Id, from: z.string().trim().min(1).max(400), to: Path }).strict(),
  output: z.object({ moved: z.number().int() }),
  successStatus: 200,
  summary: 'Rename or move a folder with everything in it (CO-0499)',
});

export const listTags = defineAction({
  name: 'knowledge.tag.list',
  method: 'get',
  path: '/knowledge/tags',
  permission: P.keys.read,
  input: z.object({ spaceId: Id.optional() }),
  output: z.object({ items: z.array(z.object({ tag: z.string(), count: z.number().int(), priority: z.number().int().describe('-2 to 2: its weight in the ranking (CO-1152)') })) }),
  summary: 'The tags in use, with their number of items and their weight in the ranking (CO-1112, CO-1152)',
});

export const prioritiseTag = defineAction({
  name: 'knowledge.tag.prioritise',
  method: 'post',
  path: '/knowledge/tags/priority',
  permission: P.keys.manage,
  input: z.object({ tag: z.string().trim().min(1).max(60), priority: z.number().int().min(-2).max(2).describe('-2 to 2; 0 takes the weight away') }).strict(),
  output: z.object({ tag: z.string(), priority: z.number().int() }),
  successStatus: 200,
  summary: 'Weigh the items with a tag heavier or lighter in search, for every space (CO-1152)',
});

export const renameTag = defineAction({
  name: 'knowledge.tag.rename',
  method: 'post',
  path: '/knowledge/tags/rename',
  permission: P.keys.update,
  input: z.object({ spaceId: Id.optional().describe('Default every space you may write in'), from: z.string().trim().min(1).max(60), to: z.string().trim().min(1).max(60) }).strict(),
  output: z.object({ changed: z.number().int() }),
  successStatus: 200,
  summary: 'Rename a tag; an existing tag as the new name merges the two (CO-1112)',
});

// ---------- search (CO-1099, CO-1119, CO-1108, CO-1157) ----------

export const SEARCH_MODES = ['fts', 'vector', 'hybrid'] as const;

const searchFilters = {
  q: z.string().trim().min(1).max(500).describe('Words; "exact phrase", -leave out and OR work. Dutch and English at once'),
  spaceId: Id.optional().describe('Only this space'),
  organisationId: Id.optional().describe('Only the space of this client'),
  projectId: Id.optional().describe('Only the space of this project'),
  type: CsvEnum(ITEM_TYPES).optional(),
  tag: z.string().max(60).optional(),
  path: z.string().max(400).optional().describe('Only this folder and the folders below it'),
  authorId: Id.optional().describe('Only what this person or agent wrote'),
  from: IsoDate.optional().describe('First day, inclusive'),
  to: IsoDate.optional().describe('Last day, inclusive'),
  dateField: z.enum(['updated', 'event']).optional().describe('What from and to filter on: the last change (default) or the event date'),
  history: QueryBool.optional().describe('Also facts that were superseded'),
  kind: CsvEnum(['item', 'document']).optional().describe('Only items or only documents; default both'),
  sourceId: Id.optional().describe('Only documents of this source'),
  limit: z.coerce.number().int().min(1).max(50).optional().describe('Default 10'),
};

export const SearchHit = z
  .object({
    kind: z.enum(['item', 'document']).describe('A knowledge item, or a document taken in from a source'),
    id: Id,
    title: z.string(),
    type: z.enum(ITEM_TYPES).nullable().describe('The type of an item; null for a document'),
    source: z.object({ id: Id, name: z.string(), kind: z.string() }).nullable().describe('The source of a document'),
    mimeType: z.string().nullable(),
    space: z.object({ id: Id, name: z.string(), kind: z.enum(SPACE_KINDS) }),
    path: z.string(),
    tags: z.array(z.string()),
    heading: z.string().describe('The section the best match is in'),
    snippet: z.string().describe('The best match, with the words found between **'),
    score: z.number(),
    updatedAt: IsoDateTime,
    eventDate: IsoDate.nullable(),
    validUntil: IsoDateTime.nullable(),
    url: z.string().describe('The deeplink to cite: the item in the app, or the original of a document'),
    appUrl: z.string().describe('The page in the app'),
  })
  .openapi('KnowledgeSearchHit');

export const searchQuery = defineAction({
  name: 'knowledge.search.query',
  method: 'get',
  path: '/knowledge/search',
  permission: P.keys.read,
  input: z.object({
    ...searchFilters,
    mode: z.enum(SEARCH_MODES).optional().describe('fts, vector or hybrid (default); without embeddings it is full-text'),
    regex: z.string().min(1).max(200).optional().describe('Also match this regular expression (case-insensitive)'),
    threshold: z.coerce.number().min(0).max(1).optional().describe('Leave out hits with a lower score (0 to 1)'),
    sort: z.enum(['relevance', 'date']).optional(),
    rrfK: z.coerce.number().int().min(1).max(500).optional().describe('Hybrid only: k of reciprocal rank fusion (default 60); for evaluation'),
    vectorWeight: z.coerce.number().min(0).max(4).optional().describe('Hybrid only: weight of meaning against words (default 1); for evaluation'),
  }),
  output: z.object({
    status: z.enum(['found', 'nothing', 'indexing']).describe('indexing: some of it is not searchable yet'),
    mode: z.enum(SEARCH_MODES).describe('The mode that answered'),
    note: z.string().nullable(),
    items: z.array(SearchHit),
    config: z
      .object({ mode: z.enum(SEARCH_MODES), model: z.string().nullable(), rrfK: z.number(), vectorWeight: z.number() })
      .describe('The configuration that answered, to keep evaluation scores per configuration (CO-1153)'),
  }),
  summary: 'Search all knowledge you may see, with the best passage per item, its score and a deeplink, without an LLM (CO-1119, CO-1099)',
});

export const recallItems = defineAction({
  name: 'knowledge.item.recall',
  method: 'get',
  path: '/knowledge/recall',
  permission: P.keys.read,
  input: z.object(searchFilters),
  output: z.object({ status: z.enum(['found', 'nothing']), items: z.array(SearchHit.extend({ text: z.string().describe('The text of the item, up to 2000 characters') })) }),
  summary: 'Find back memories, decisions and notes with their text; only current facts unless history is asked (CO-1108, CO-1157)',
});

// ---------- sources, documents and ingest runs (CO-1096, CO-1091, CO-1092, CO-1104, CO-1131, CO-1143, CO-1173) ----------

export const SOURCE_KINDS = ['upload', 'tickets', 'graph-mail', 'graph-files', 'forgejo', 'meetings', 'app'] as const;
export const SOURCE_STATUSES = ['active', 'paused', 'failed'] as const;
export const DOCUMENT_STATUSES = ['indexed', 'failed'] as const;
export const RUN_KINDS = ['upload', 'sync', 'backfill', 'retry'] as const;
export const RUN_STATUSES = ['running', 'succeeded', 'partial', 'failed'] as const;
export const RUN_OUTCOMES = ['indexed', 'unchanged', 'skipped', 'failed', 'deleted'] as const;

export const MAIL_PARTS = ['subject', 'body', 'attachments'] as const;
export const FORGE_PARTS = ['files', 'wiki', 'issues', 'pulls'] as const;
export const APP_RECORDS = ['organisation', 'engagement', 'proposal', 'project'] as const;

const Glob = z.string().trim().min(1).max(300);
const MailboxScope = z
  .object({
    address: z.string().trim().toLowerCase().email().max(200).describe('The M365 mailbox'),
    folders: z
      .array(z.string().trim().min(1).max(200))
      .min(1)
      .max(20)
      .optional()
      .describe('Folder ids or well-known names (inbox, sentitems, archive); default inbox and sentitems'),
  })
  .strict();
const DriveScope = z
  .object({
    site: z
      .string()
      .trim()
      .max(300)
      .optional()
      .describe('A SharePoint site as host:/sites/name, such as contoso.sharepoint.com:/sites/klanten'),
    library: z
      .string()
      .trim()
      .max(200)
      .optional()
      .describe('A document library of the site by name; default its default library'),
    user: z
      .string()
      .trim()
      .toLowerCase()
      .email()
      .max(200)
      .optional()
      .describe('The OneDrive of this user instead of a site'),
    folder: z
      .string()
      .trim()
      .max(500)
      .optional()
      .describe('Only this folder and what is in it, such as /Klanten'),
    pages: z.boolean().optional().describe('Also the pages of the site'),
  })
  .strict()
  .refine((d) => !!d.site !== !!d.user, { message: 'Give a site or a user, not both' });
const RepoScope = z
  .object({
    repo: z
      .string()
      .trim()
      .regex(/^[^/\s]+\/[^/\s]+$/, 'owner/name')
      .describe('A repository of the issue mirror, as owner/name'),
    branch: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .optional()
      .describe('The branch of the files; default the default branch'),
    parts: z
      .array(z.enum(FORGE_PARTS))
      .min(1)
      .optional()
      .describe('What to take in; default all: files, wiki, issues and pulls'),
  })
  .strict();

export const SourceConfig = z
  .object({
    include: z.array(Glob).max(50).optional().describe('Only documents whose path or title matches one of these patterns (* within a folder, ** across folders)'),
    exclude: z.array(Glob).max(50).optional().describe('Leave out documents whose path or title matches one of these patterns'),
    startDate: IsoDate.optional().describe('Only documents changed on or after this day: the horizon of the backfill'),
    maskPersonalData: z.boolean().optional().describe('Mask e-mail addresses, phone numbers and IBANs before indexing (CO-1173)'),
    ticketStatuses: z.array(z.string().max(30)).max(10).optional().describe('tickets: the statuses to take in; default solved and closed'),
    mailboxes: z.array(MailboxScope).min(1).max(20).optional().describe('graph-mail: the mailboxes and their folders (CO-1110)'),
    mailParts: z.array(z.enum(MAIL_PARTS)).min(1).optional().describe('graph-mail: what of a message is taken in; default subject and body'),
    keepSignatures: z.boolean().optional().describe('graph-mail: keep signatures; by default they are cut off, like quoted earlier messages'),
    locations: z.array(DriveScope).min(1).max(20).optional().describe('graph-files: SharePoint sites and libraries or OneDrives, with a folder (CO-1116)'),
    repositories: z.array(RepoScope).min(1).max(50).optional().describe('forgejo: the repositories with branch and parts (CO-1098)'),
    records: z.array(z.enum(APP_RECORDS)).min(1).optional().describe('app: the kinds of records; default all (CO-1123)'),
  })
  .strict();

const SpaceRef = z.object({ id: Id, name: z.string(), kind: z.enum(SPACE_KINDS) });

export const KnowledgeSource = z
  .object({
    id: Id,
    kind: z.enum(SOURCE_KINDS),
    name: z.string(),
    spaceId: Id.describe('Where its documents go, unless a document belongs to a client or project: then to that space'),
    space: SpaceRef,
    config: SourceConfig,
    localOnly: z.boolean().describe('Its text goes to no external model'),
    priority: z.number().int().describe('-2 to 2: weighs its documents in the ranking (CO-1152)'),
    syncEveryMinutes: z.number().int().nullable().describe('How often it syncs; null: only by hand'),
    status: z.enum(SOURCE_STATUSES),
    lastSyncAt: IsoDateTime.nullable(),
    lastError: z.string().nullable(),
    failureCount: z.number().int(),
    documents: z.number().int(),
    bytes: z.number().int(),
    byType: z.array(z.object({ mimeType: z.string(), documents: z.number().int(), bytes: z.number().int() })).describe('The inventory per kind of content (CO-1131)'),
    version: z.number().int(),
  })
  .openapi('KnowledgeSource');

const sourceFields = {
  name: z.string().trim().min(1).max(200),
  config: SourceConfig.optional(),
  localOnly: z.boolean().optional(),
  priority: z.number().int().min(-2).max(2).optional(),
  syncEveryMinutes: z.number().int().min(15).max(10_080).nullable().optional(),
};

export const listSources = defineAction({
  name: 'knowledge.source.list',
  method: 'get',
  path: '/knowledge/sources',
  permission: P.keys.read,
  input: z.object({ spaceId: Id.optional(), kind: CsvEnum(SOURCE_KINDS).optional() }),
  output: z.object({ items: z.array(KnowledgeSource) }),
  summary: 'The sources of knowledge with their status, last sync and what they hold per kind of content (CO-1131)',
});

export const getSource = defineAction({
  name: 'knowledge.source.get',
  method: 'get',
  path: '/knowledge/sources/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: KnowledgeSource,
  etag: true,
  summary: 'One source',
});

export const createSource = defineAction({
  name: 'knowledge.source.create',
  method: 'post',
  path: '/knowledge/sources',
  permission: P.keys.manage,
  input: z
    .object({
      kind: z
        .enum(SOURCE_KINDS)
        .describe(
          'upload is made by itself on the first upload; tickets: the solved tickets of support; graph-mail: mailboxes (config.mailboxes); graph-files: SharePoint and OneDrive (config.locations); forgejo: repositories of the mirror (config.repositories); meetings: meetings with a report or transcript; app: organisations, engagements, proposals and projects (config.records)',
        ),
      spaceId: Id.optional().describe('The space its documents go to when they belong to no client or project; default the company space'),
      ...sourceFields,
    })
    .strict(),
  output: KnowledgeSource,
  summary: 'Connect a source, with its scope (folders, patterns, start date) and how often it syncs (CO-1091)',
});

export const updateSource = defineAction({
  name: 'knowledge.source.update',
  method: 'patch',
  path: '/knowledge/sources/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, ...updateFields(sourceFields), expectedVersion: ExpectedVersion }).strict(),
  output: KnowledgeSource,
  etag: true,
  summary: 'Change the scope, priority or schedule of a source; the next sync applies it',
});

export const IngestRun = z
  .object({
    id: Id,
    sourceId: Id,
    source: z.object({ id: Id, name: z.string(), kind: z.enum(SOURCE_KINDS) }),
    kind: z.enum(RUN_KINDS),
    status: z.enum(RUN_STATUSES),
    dryRun: z.boolean().describe('Nothing was written: what a sync would do'),
    counts: z.object({ seen: z.number().int(), indexed: z.number().int(), unchanged: z.number().int(), skipped: z.number().int(), failed: z.number().int(), deleted: z.number().int() }),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
    error: z.string().nullable(),
    workflowId: z.string().nullable().describe('The job run (admin.job.get)'),
  })
  .openapi('KnowledgeIngestRun');

export const syncSource = defineAction({
  name: 'knowledge.source.sync',
  method: 'post',
  path: '/knowledge/sources/{id}/sync',
  permission: P.keys.manage,
  input: z
    .object({
      id: Id,
      mode: z.enum(['incremental', 'backfill']).optional().describe('incremental (default): what changed since the last sync; backfill: everything from the start date'),
      dryRun: z.boolean().optional().describe('Count what would happen, write nothing; for a large import first'),
    })
    .strict(),
  output: IngestRun,
  successStatus: 202,
  summary: 'Sync a source now, or try what a sync would do (CO-1092, CO-1173)',
});

export const pauseSource = defineAction({
  name: 'knowledge.source.pause',
  method: 'post',
  path: '/knowledge/sources/{id}/pause',
  permission: P.keys.manage,
  input: z.object({ id: Id, paused: z.boolean().default(true) }).strict(),
  output: KnowledgeSource,
  successStatus: 200,
  summary: 'Pause a source (or resume it): its documents stay, it no longer syncs',
});

export const disconnectSource = defineAction({
  name: 'knowledge.source.disconnect',
  method: 'delete',
  path: '/knowledge/sources/{id}',
  permission: P.keys.manage,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ deletedDocuments: z.number().int() }),
  successStatus: 200,
  etag: true,
  kind: 'destructive',
  summary: 'Disconnect a source: it and all its documents, chunks and stored files are deleted (CO-1131)',
});

export const listRuns = defineAction({
  name: 'knowledge.ingest.list',
  method: 'get',
  path: '/knowledge/ingest-runs',
  permission: P.keys.read,
  input: ListQuery.extend({ sourceId: Id.optional(), status: CsvEnum(RUN_STATUSES).optional() }),
  output: Paginated(IngestRun),
  summary: 'The ingest runs, newest first, with their counters (CO-1143)',
});

export const getRun = defineAction({
  name: 'knowledge.ingest.get',
  method: 'get',
  path: '/knowledge/ingest-runs/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: IngestRun.extend({
    documents: z.array(z.object({ externalId: z.string(), title: z.string(), documentId: Id.nullable(), outcome: z.enum(RUN_OUTCOMES), reason: z.string().nullable() })).describe('Per document what happened, failures first'),
  }),
  summary: 'One ingest run with per document its outcome and the reason of a failure or skip (CO-1143)',
});

export const retryRun = defineAction({
  name: 'knowledge.ingest.retry',
  method: 'post',
  path: '/knowledge/ingest-runs/{id}/retry',
  permission: P.keys.manage,
  input: z.object({ id: Id }).strict(),
  output: IngestRun,
  successStatus: 202,
  summary: 'Try the failed documents of a run again, in a new run (CO-1143)',
});

export const KnowledgeDocument = z
  .object({
    id: Id,
    sourceId: Id,
    source: z.object({ id: Id, name: z.string(), kind: z.enum(SOURCE_KINDS) }).nullable().describe('null when the source is in a space you do not see, such as a shared one of the ticket source'),
    spaceId: Id,
    space: SpaceRef,
    externalId: z.string(),
    title: z.string(),
    url: z.string().nullable().describe('The original, to cite'),
    path: z.string(),
    mimeType: z.string(),
    author: z.string().nullable(),
    language: z.enum(['dutch', 'english']),
    size: z.number().int().describe('Bytes of the original'),
    status: z.enum(DOCUMENT_STATUSES),
    error: z.string().nullable(),
    hidden: z.boolean().describe('Out of search for now, not deleted (CO-1152)'),
    boost: z.number().describe('-1 to 1: weighs it in the ranking (CO-1152)'),
    metadata: z.record(z.string(), z.unknown()),
    createdAtSource: IsoDateTime.nullable(),
    updatedAtSource: IsoDateTime.nullable(),
    indexedAt: IsoDateTime,
    fileUrl: z.string().nullable().describe('The stored original of an upload'),
    appUrl: z.string(),
    version: z.number().int(),
  })
  .openapi('KnowledgeDocument');

export const listDocuments = defineAction({
  name: 'knowledge.document.list',
  method: 'get',
  path: '/knowledge/documents',
  permission: P.keys.read,
  input: ListQuery.extend({
    spaceId: Id.optional(),
    sourceId: Id.optional(),
    path: z.string().max(400).optional(),
    recursive: QueryBool.optional(),
    status: CsvEnum(DOCUMENT_STATUSES).optional(),
    hidden: QueryBool.optional(),
    title: z.string().max(300).optional().describe('Words in the title'),
    mimeType: z.string().max(100).optional(),
  }),
  output: Paginated(KnowledgeDocument),
  summary: 'Browse the documents taken in, per space, source and folder, newest first (CO-1095, CO-1127)',
});

export const getDocument = defineAction({
  name: 'knowledge.document.get',
  method: 'get',
  path: '/knowledge/documents/{id}',
  permission: P.keys.read,
  input: z.object({
    id: Id,
    from: z.coerce.number().int().min(0).optional().describe('First character of the text to return, for long documents'),
    to: z.coerce.number().int().min(1).optional(),
  }),
  output: KnowledgeDocument.extend({ text: z.string().describe('The text as indexed: markdown, cleaned'), length: z.number().int() }),
  etag: true,
  summary: 'One document with its text, or a range of a long one (CO-1095)',
});

export const upsertDocument = defineAction({
  name: 'knowledge.document.upsert',
  method: 'post',
  path: '/knowledge/documents',
  permission: P.keys.create,
  input: z
    .object({
      spaceId: Id.optional(),
      organisationId: Id.optional(),
      projectId: Id.optional(),
      title: z.string().trim().min(1).max(300).optional().describe('Default the name of the page or the first line'),
      text: z.string().min(1).max(2_000_000).optional().describe('Pasted text or markdown'),
      url: z.string().url().max(2000).optional().describe('A page or file to fetch; internal addresses are refused'),
      path: z.string().trim().max(400).optional(),
      externalId: z.string().trim().max(300).optional().describe('Your own id; default the URL, or the title and folder'),
      metadata: z.record(z.string(), z.unknown()).optional(),
      replace: z.boolean().optional().describe('Overwrite a document with the same id; without it that is a conflict'),
      dryRun: z.boolean().optional(),
    })
    .strict(),
  output: z.object({ outcome: z.enum(RUN_OUTCOMES), reason: z.string().nullable(), document: KnowledgeDocument.nullable(), runId: Id }),
  summary: 'Take in a text or a URL as a document of a space: cleaned, chunked and searchable at once (CO-1104, CO-1129)',
});

export const updateDocument = defineAction({
  name: 'knowledge.document.update',
  method: 'patch',
  path: '/knowledge/documents/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id, hidden: z.boolean().optional(), boost: z.number().min(-1).max(1).optional(), expectedVersion: ExpectedVersion }).strict(),
  output: KnowledgeDocument,
  etag: true,
  summary: 'Hide a document from search for now, or boost it (CO-1152)',
});

export const reparseDocument = defineAction({
  name: 'knowledge.document.reparse',
  method: 'post',
  path: '/knowledge/documents/{id}/reparse',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: z.object({ outcome: z.enum(RUN_OUTCOMES), reason: z.string().nullable(), document: KnowledgeDocument.nullable(), runId: Id }),
  successStatus: 200,
  summary: 'Read a document again from its original and index it again (CO-1143)',
});

export const deleteDocument = defineAction({
  name: 'knowledge.document.delete',
  method: 'delete',
  path: '/knowledge/documents/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ deleted: z.boolean() }),
  successStatus: 200,
  etag: true,
  kind: 'destructive',
  summary: 'Delete an uploaded document with its chunks and stored original (CO-1104)',
});

// ---------- the index (CO-1097, CO-1102) ----------

const IndexStatus = z.object({
  model: z.string().nullable().describe('The embedding model now (AI_MODEL_EMBED); null: full-text only'),
  chunks: z.number().int(),
  items: z.number().int(),
  embedded: z.number().int().describe('Chunks with a vector of the current model'),
  skipped: z.number().int().describe('Chunks of local-only spaces the model may not see'),
  pending: z.number().int().describe('Chunks that still wait for a vector'),
});

export const indexStatus = defineAction({
  name: 'knowledge.index.status',
  method: 'get',
  path: '/knowledge/index',
  permission: P.keys.read,
  input: z.object({ spaceId: Id.optional() }),
  output: IndexStatus,
  summary: 'How far the search index is: chunks, with a vector of the current model, skipped and still to do (CO-1102)',
});

export const rebuildIndex = defineAction({
  name: 'knowledge.index.rebuild',
  method: 'post',
  path: '/knowledge/index/rebuild',
  permission: P.keys.manage,
  input: z
    .object({
      spaceId: Id.optional().describe('Only this space; default all'),
      sourceId: Id.optional().describe('Only the documents of this source'),
      rechunk: z.boolean().optional().describe('Chunk the texts again too, after a change of chunking'),
      graph: z.boolean().optional().describe('Derive the knowledge graph again too (CO-1096)'),
    })
    .strict(),
  output: IndexStatus.extend({ runId: z.string().describe('Follow it with admin.job.get') }),
  successStatus: 202,
  summary: 'Rebuild the search index next to the old one: vectors of the current model for every chunk, and new chunks and the graph when asked (CO-1102, CO-1096)',
});

// ---------- provenance and forgetting (CO-1093, CO-1141, CO-1118) ----------

export const PROVENANCE_DERIVED = ['chunk', 'item', 'entity', 'observation', 'relation', 'summary'] as const;
export const PROVENANCE_SOURCES = ['item', 'document', 'question'] as const;
export const RUN_REFS = ['ingest', 'agent'] as const;
const ObjectRef = z.object({ type: z.enum(['item', 'document']), id: Id });

export const getProvenance = defineAction({
  name: 'knowledge.provenance.get',
  method: 'get',
  path: '/knowledge/provenance',
  permission: P.keys.read,
  input: z.object({ type: z.enum(['chunk', 'item', 'document', 'entity', 'observation', 'relation', 'summary', 'question']), id: Id }),
  output: z.object({
    type: z.string(),
    id: Id,
    sources: z
      .array(z.object({ type: z.enum(PROVENANCE_SOURCES), id: Id, title: z.string().nullable(), url: z.string().nullable(), run: z.object({ kind: z.enum(RUN_REFS), id: Id }).nullable() }))
      .describe('What it was made from, with the run that made it; a source you may not see has no title'),
    derived: z.array(z.object({ type: z.enum(PROVENANCE_DERIVED), id: Id, title: z.string().nullable() })).describe('What was made from it, chunks left out'),
    chunks: z.number().int().describe('Its chunks in the index'),
  }),
  summary: 'Where a piece of knowledge comes from and which run made it, and what was derived from it (CO-1093, CO-1141)',
});

const forgetScope = {
  ids: z.array(ObjectRef).min(1).max(500).optional().describe('Items and documents, as chosen from a preview'),
  sourceId: Id.optional().describe('Every document of this source; the source stays, but its forgotten documents never come back'),
  spaceId: Id.optional().describe('Everything in this space'),
  organisationId: Id.optional().describe('Everything in the spaces of this client and its projects, as for a GDPR request'),
};
const ForgetCounts = z.object({
  items: z.number().int(),
  documents: z.number().int(),
  chunks: z.number().int(),
  derivedRemoved: z.number().int().describe('Derived objects that rested on these sources only and go too'),
  derivedKept: z.number().int().describe('Derived objects that stay because another source carries them; only their provenance changes'),
  questions: z.number().int().describe('Questions asked in a forgotten space, with their answers (CO-1162)'),
});

export const previewForget = defineAction({
  name: 'knowledge.forget.preview',
  method: 'post',
  path: '/knowledge/forget/preview',
  permission: P.keys.delete,
  input: z
    .object({
      q: z.string().trim().min(1).max(500).optional().describe('Words, as in search: the matches are what would go. Without q: the ids, source, space or client'),
      ...forgetScope,
      projectId: Id.optional().describe('With q: only the space of this project'),
      kind: CsvEnum(['item', 'document']).optional().describe('With q: only items or only documents'),
      limit: z.number().int().min(1).max(100).optional().describe('With q: at most this many matches; default 50'),
    })
    .strict(),
  output: z.object({
    targets: z.array(
      z.object({
        type: z.enum(['item', 'document']),
        id: Id,
        title: z.string(),
        url: z.string().nullable(),
        space: SpaceRef,
        chunks: z.number().int(),
        derivedRemoved: z.number().int(),
        derivedKept: z.number().int(),
      }),
    ),
    totals: ForgetCounts,
    truncated: z.boolean().describe('More than 500 objects: the totals count all, the list shows the first 500'),
  }),
  successStatus: 200,
  kind: 'read',
  summary: 'What forgetting would delete: per item and document its chunks and what was derived from it, nothing deleted yet (CO-1118)',
});

export const applyForget = defineAction({
  name: 'knowledge.forget.apply',
  method: 'post',
  path: '/knowledge/forget',
  permission: P.keys.delete,
  input: z.object({ ...forgetScope, reason: z.string().trim().max(500).optional().describe('Why, for the audit; no personal data') }).strict(),
  output: ForgetCounts.extend({
    status: z.enum(['done', 'started']).describe('started: many objects, forgotten in a job'),
    tombstones: z.number().int().describe('Documents of a source that may not come back'),
    runId: z.string().nullable().describe('The job when started; follow it with admin.job.get'),
  }),
  successStatus: 200,
  kind: 'destructive',
  summary: 'Forget items and documents for good, with their versions, chunks and what was derived from them only; documents of a source get a tombstone (CO-1118)',
});

// ---------- context, ask, questions, activity and the brief (CO-1134, CO-1087, CO-1156, CO-1155, CO-1162, CO-1088, CO-1090) ----------

export const QUESTION_STATUSES = ['answered', 'no-answer', 'no-model', 'failed'] as const;
export const RATINGS = ['good', 'bad'] as const;
const scopeFilters = {
  spaceId: Id.optional().describe('Only this space'),
  organisationId: Id.optional().describe('Only the space of this client'),
  projectId: Id.optional().describe('Only the space of this project'),
};
const ContextSource = z.object({
  n: z.number().int().describe('The number of the passage in the text: [1], [2]'),
  kind: z.enum(['item', 'document']),
  id: Id,
  title: z.string(),
  url: z.string(),
  space: SpaceRef,
  score: z.number(),
  updatedAt: IsoDateTime,
});

export const searchContext = defineAction({
  name: 'knowledge.search.context',
  method: 'get',
  path: '/knowledge/context',
  permission: P.keys.read,
  input: z.object({
    q: z.string().trim().min(1).max(500).describe('What the context is for: words or a question'),
    ...scopeFilters,
    kind: CsvEnum(['item', 'document']).optional(),
    maxTokens: z.coerce.number().int().min(100).max(8000).optional().describe('The budget; default 2000 (about four characters a token)'),
    recent: QueryBool.optional().describe('The most recent first instead of the best match'),
  }),
  output: z.object({
    text: z.string().describe('The best passages as one text, each with its number, title and link'),
    sources: z.array(ContextSource),
    tokens: z.number().int().describe('An estimate of the size of text'),
    truncated: z.boolean().describe('More passages were found than fit'),
    mode: z.enum(['fts', 'vector', 'hybrid']),
    note: z.string().nullable(),
  }),
  summary: 'The best passages of items and documents as one context text within a token budget, with numbered sources (CO-1134)',
});

export const askQuestion = defineAction({
  name: 'knowledge.search.ask',
  method: 'post',
  path: '/knowledge/ask',
  permission: P.keys.read,
  input: z
    .object({
      question: z.string().trim().min(3).max(2000),
      ...scopeFilters,
      kind: CsvEnum(['item', 'document']).optional(),
      strict: z.boolean().optional().describe('Only from what is found, also where the space allows more (CO-1156)'),
      maxTokens: z.number().int().min(200).max(8000).optional().describe('The budget of the passages given to the model; default 3000'),
    })
    .strict(),
  output: z.object({
    questionId: Id,
    status: z.enum(QUESTION_STATUSES).describe('no-answer: nothing found that answers it; no-model: no model set, the passages come without an answer'),
    answer: z.string(),
    grounded: z.boolean().describe('The answer rests on the sources only'),
    sources: z.array(ContextSource).describe('The passages the answer cites; with no-model all found passages'),
    note: z.string().nullable(),
  }),
  successStatus: 200,
  kind: 'read',
  summary: 'A question about all knowledge you may see, answered with numbered sources, or a fixed no-answer when nothing found answers it (CO-1087, CO-1156, CO-1155)',
});

export const KnowledgeQuestion = z
  .object({
    id: Id,
    question: z.string(),
    answer: z.string(),
    status: z.enum(QUESTION_STATUSES),
    grounded: z.boolean(),
    sources: z.array(z.object({ kind: z.enum(['item', 'document']), id: Id, title: z.string() })),
    space: SpaceRef.nullable().describe('The space it was asked in; null: everything the asker sees'),
    askedBy: z.object({ id: Id, name: z.string(), kind: z.enum(['human', 'agent']) }),
    modelId: z.string().nullable(),
    runId: Id.nullable().describe('The AgentRun of the answer, with its costs'),
    rating: z.enum(RATINGS).nullable(),
    comment: z.string().nullable(),
    createdAt: IsoDateTime,
  })
  .openapi('KnowledgeQuestion');

export const listQuestions = defineAction({
  name: 'knowledge.question.list',
  method: 'get',
  path: '/knowledge/questions',
  permission: P.keys.read,
  input: ListQuery.extend({
    askedBy: Id.optional().describe('Only of this person or agent; without knowledge:manage only your own'),
    actorKind: z.enum(['human', 'agent']).optional(),
    spaceId: Id.optional(),
    status: CsvEnum(QUESTION_STATUSES).optional(),
    rating: z.enum(['good', 'bad', 'none']).optional().describe('none: not rated yet'),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
  }),
  output: Paginated(KnowledgeQuestion),
  summary: 'The questions asked with their answer, sources, asker and feedback, newest first; who manages knowledge sees all (CO-1162)',
});

export const rateQuestion = defineAction({
  name: 'knowledge.question.feedback',
  method: 'post',
  path: '/knowledge/questions/{id}/feedback',
  permission: P.keys.read,
  input: z.object({ id: Id, rating: z.enum(RATINGS), comment: z.string().trim().max(2000).optional() }).strict(),
  output: KnowledgeQuestion,
  successStatus: 200,
  summary: 'Rate an answer as good or bad, with a comment: the asker or who manages knowledge (CO-1162)',
});

export const ACTIVITY_KINDS = ['item', 'document', 'run', 'forget'] as const;
export const ActivityEntry = z.object({
  at: IsoDateTime,
  kind: z.enum(ACTIVITY_KINDS),
  action: z.string().describe('Such as created, updated, archived, indexed, deleted, forgotten, or the status of a run'),
  id: Id.nullable().describe('The item, document or run; null for a forget'),
  title: z.string().nullable().describe('null when it is gone or forgotten'),
  url: z.string().nullable(),
  space: SpaceRef.nullable(),
  actor: z.object({ kind: z.enum(['human', 'agent', 'system']), id: Id.nullable(), name: z.string().nullable() }),
  detail: z.string().nullable(),
});

export const listActivity = defineAction({
  name: 'knowledge.activity.list',
  method: 'get',
  path: '/knowledge/activity',
  permission: P.keys.read,
  input: ListQuery.extend({
    ...scopeFilters,
    kind: CsvEnum(ACTIVITY_KINDS).optional(),
    actorKind: z.enum(['human', 'agent', 'system']).optional(),
    since: IsoDateTime.optional().describe('Only after this moment'),
  }),
  output: Paginated(ActivityEntry),
  summary: 'What changed in knowledge, newest first: items written, documents taken in or deleted, ingest runs and forgetting, by people and agents (CO-1088)',
});

export const sessionBrief = defineAction({
  name: 'knowledge.session.brief',
  method: 'get',
  path: '/knowledge/brief',
  permission: P.keys.read,
  input: z.object({
    ...scopeFilters,
    since: z
      .string()
      .regex(/^(\d{1,3}d|\d{4}-\d{2}-\d{2})$/, '7d or a date')
      .optional()
      .describe('How far back changes go: 1d, 7d (default), 30d or a date'),
    maxTokens: z.coerce.number().int().min(200).max(4000).optional().describe('Default 1500'),
  }),
  output: z.object({
    text: z.string().describe('The brief as markdown with links, within the budget'),
    space: SpaceRef.nullable(),
    since: IsoDateTime,
    instructions: z.array(z.object({ space: SpaceRef, text: z.string() })),
    tasks: z.array(z.object({ key: z.string(), title: z.string(), status: z.string(), dueOn: IsoDate.nullable(), project: z.string().nullable().describe('null for a task without a project'), url: z.string() })),
    issues: z.array(z.object({ ref: z.string(), title: z.string(), url: z.string() })),
    decisions: z.array(z.object({ id: Id, title: z.string(), url: z.string(), updatedAt: IsoDateTime })).describe('The five newest current decisions'),
    procedures: z
      .array(z.object({ id: Id, type: z.enum(['procedure', 'policy']), title: z.string(), url: z.string(), updatedAt: IsoDateTime }))
      .optional()
      .describe('The three newest current procedures and policies (#1395)'),
    handovers: z.array(z.object({ id: Id, title: z.string(), url: z.string(), updatedAt: IsoDateTime, excerpt: z.string() })).describe('The newest handover per repository and branch, at most three'),
    sessions: z.array(z.object({ id: Id, title: z.string(), url: z.string(), updatedAt: IsoDateTime, excerpt: z.string() })).describe('The two newest session logs (CO-1376)'),
    changes: z.object({ count: z.number().int(), recent: z.array(ActivityEntry) }),
    truncated: z.boolean(),
  }),
  summary: 'A short brief at the start of a session: instructions, open tasks and issues, current decisions, procedures and policies, handovers and session logs, and what changed, with links; superseded, expired and consolidated items stay out (CO-1090, CO-1088, CO-1155, CO-1376)',
});

// ---------- links, backlinks and related knowledge (CO-0502, CO-1139) ----------

export const LINK_KINDS = ['item', 'organisation', 'project', 'issue'] as const;

export const itemLinks = defineAction({
  name: 'knowledge.item.links',
  method: 'get',
  path: '/knowledge/items/{id}/links',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({
    links: z.array(
      z.object({
        target: z.string().describe('As written between [[ ]]: a title, org:<id>, project:<id> or issue:owner/name#n'),
        label: z.string().nullable(),
        kind: z.enum(LINK_KINDS),
        id: z.string().nullable().describe('The item, organisation or project; null for an issue or a link to an item that does not exist yet'),
        title: z.string().nullable(),
        url: z.string().nullable().describe('The page in the app, or the issue in the forge'),
        status: z.string().nullable().describe('The live state of an issue'),
        exists: z.boolean().describe('false: a link to an item that does not exist yet; opening it makes it'),
      }),
    ),
    backlinks: z.array(z.object({ id: Id, title: z.string(), url: z.string(), space: SpaceRef })),
  }),
  summary: 'The [[links]] of an item resolved to items, organisations, projects and issues with their live state, and the items that link to it (CO-0502)',
});

export const RELATED_TYPES = ['organisation', 'project', 'ticket', 'item', 'document'] as const;
export const RelatedHit = z.object({
  kind: z.enum(['item', 'document']),
  id: Id,
  title: z.string(),
  type: z.enum(ITEM_TYPES).nullable(),
  sourceKind: z.string().nullable().describe('The kind of source of a document, such as meetings or graph-mail'),
  space: SpaceRef,
  updatedAt: IsoDateTime,
  snippet: z.string().nullable(),
  url: z.string(),
  appUrl: z.string(),
  reason: z.enum(['space', 'similar']).describe('space: in the space of the client or project; similar: found by its meaning'),
});

export const relatedKnowledge = defineAction({
  name: 'knowledge.related.list',
  method: 'get',
  path: '/knowledge/related',
  permission: P.keys.read,
  input: z.object({
    type: z.enum(RELATED_TYPES),
    id: Id,
    q: z.string().trim().max(500).optional().describe('What it is about, such as the subject of a ticket: found by meaning'),
    limit: z.coerce.number().int().min(1).max(30).optional().describe('Default 10'),
  }),
  output: z.object({ items: z.array(RelatedHit) }),
  summary: 'Knowledge next to a client, project, ticket, item or document: what is in its space, such as every meeting with the client, and what is similar in meaning (CO-1139)',
});

// ---------- the graph and the types (CO-1144, CO-1145, CO-1136, CO-1137, CO-1140, CO-1146, CO-1150, CO-1151) ----------

export const ENTITY_KINDS = ['person', 'organisation', 'project', 'engagement', 'issue', 'meeting', 'topic', 'decision', 'procedure', 'preference'] as const;
export const APP_REFS = ['organisation', 'contact', 'project', 'engagement', 'issue', 'meeting', 'user'] as const;
const SourceRef = z.object({ type: z.enum(PROVENANCE_SOURCES), id: Id, title: z.string().nullable() });

export const KnowledgeEntity = z
  .object({
    id: Id,
    kind: z.enum(ENTITY_KINDS),
    name: z.string(),
    aliases: z.array(z.string()),
    emails: z.array(z.string()),
    app: z.object({ type: z.enum(APP_REFS), ref: z.string(), url: z.string().nullable() }).nullable().describe('The record of the app it stands for: a client, contact, project, engagement, issue, meeting or user'),
    itemId: Id.nullable().describe('The knowledge item it stands for'),
    space: SpaceRef,
    attributes: z.record(z.string(), z.unknown()),
    counts: z.object({ observations: z.number().int(), relations: z.number().int(), sources: z.number().int() }),
    mergedInto: Id.nullable(),
    updatedAt: IsoDateTime,
  })
  .openapi('KnowledgeEntity');

export const listEntities = defineAction({
  name: 'knowledge.entity.list',
  method: 'get',
  path: '/knowledge/entities',
  permission: P.keys.read,
  input: ListQuery.extend({
    kind: CsvEnum(ENTITY_KINDS).optional(),
    q: z.string().trim().max(200).optional().describe('Words in the name, an alias or an e-mail address'),
    name: z.string().trim().max(300).optional().describe('Exactly this name or alias (case does not matter)'),
    spaceId: Id.optional(),
    appType: z.enum(APP_REFS).optional(),
    appRef: z.string().max(300).optional(),
    itemId: Id.optional().describe('The entity of this knowledge item'),
    from: IsoDate.optional().describe('Changed on or after this day (Europe/Amsterdam)'),
    to: IsoDate.optional().describe('Changed on or before this day (Europe/Amsterdam)'),
    sort: z.enum(['name', 'date_asc', 'date_desc']).optional().describe('Default name; date sorts on the day it last changed'),
  }),
  output: Paginated(KnowledgeEntity),
  summary: 'The entities of the knowledge graph per kind (people, organisations, projects, meetings, topics) with their counts, or one by name (CO-1136)',
});

export const getEntity = defineAction({
  name: 'knowledge.entity.get',
  method: 'get',
  path: '/knowledge/entities/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: KnowledgeEntity.extend({
    observations: z.array(z.object({ id: Id, category: z.string(), content: z.string(), tags: z.array(z.string()), source: SourceRef.nullable() })),
    relations: z.array(
      z.object({
        id: Id,
        direction: z.enum(['out', 'in']),
        type: z.string(),
        other: z.object({ id: Id.nullable(), name: z.string(), kind: z.enum(ENTITY_KINDS).nullable() }),
        source: SourceRef.nullable(),
      }),
    ),
    sources: z.array(SourceRef).describe('The items and documents it was derived from'),
  }),
  summary: 'One entity with its observations, its relations both ways and the sources it comes from (CO-1136, CO-1144)',
});

export const entityNeighbours = defineAction({
  name: 'knowledge.entity.neighbours',
  method: 'get',
  path: '/knowledge/entities/{id}/neighbours',
  permission: P.keys.read,
  input: z.object({ id: Id, depth: z.coerce.number().int().min(1).max(3).optional().describe('Default 1'), types: z.string().max(300).optional().describe('Only these relation types, comma separated') }),
  output: z.object({
    nodes: z.array(z.object({ id: Id, kind: z.enum(ENTITY_KINDS), name: z.string(), depth: z.number().int() })),
    edges: z.array(z.object({ from: Id, to: Id, type: z.string() })),
    observations: z.array(z.object({ entityId: Id, category: z.string(), content: z.string() })),
    truncated: z.boolean().describe('More than 200 entities: the nearest are shown'),
  }),
  summary: 'The neighbourhood of an entity in the graph up to a depth: connected entities, the relations and their observations (CO-1140)',
});

export const graphStats = defineAction({
  name: 'knowledge.graph.stats',
  method: 'get',
  path: '/knowledge/graph/stats',
  permission: P.keys.read,
  input: z.object({ spaceId: Id.optional(), kind: CsvEnum(ENTITY_KINDS).optional(), entityId: Id.optional().describe('Only what is connected to this entity') }),
  output: z.object({
    entities: z.array(z.object({ kind: z.enum(ENTITY_KINDS), count: z.number().int() })),
    relations: z.array(z.object({ type: z.string(), count: z.number().int() })),
    observations: z.array(z.object({ category: z.string(), count: z.number().int() })),
  }),
  summary: 'Simple counts over the graph: entities per kind, relations per type, observations per category (CO-1140)',
});

const graphSources = z
  .array(z.string().regex(/^(item|document|question):[0-9a-f-]{36}$/i, 'item:<id>, document:<id> or question:<id>'))
  .max(20)
  .optional()
  .describe('Where it comes from, kept as provenance: item:<id>, document:<id>, question:<id>');

export const upsertEntity = defineAction({
  name: 'knowledge.entity.upsert',
  method: 'post',
  path: '/knowledge/entities',
  permission: P.keys.update,
  input: z
    .object({
      kind: z.enum(ENTITY_KINDS),
      name: z.string().trim().min(1).max(300),
      aliases: z.array(z.string().trim().min(1).max(300)).max(20).optional(),
      emails: z.array(z.string().trim().toLowerCase().email()).max(20).optional(),
      app: z.object({ type: z.enum(APP_REFS), ref: z.string().trim().min(1).max(300) }).optional().describe('The record of the app it stands for'),
      attributes: z.record(z.string(), z.unknown()).optional(),
      spaceId: Id.optional(),
      organisationId: Id.optional(),
      projectId: Id.optional(),
      sources: graphSources,
    })
    .strict(),
  output: KnowledgeEntity.extend({ outcome: z.enum(['created', 'updated']) }),
  successStatus: 200,
  summary: 'Add an entity to the graph, or find the one with the same record, e-mail or name and add to it (CO-1146, CO-1137)',
});

export const mergeEntity = defineAction({
  name: 'knowledge.entity.merge',
  method: 'post',
  path: '/knowledge/entities/{id}/merge',
  permission: P.keys.update,
  input: z.object({ id: Id.describe('The entity that goes'), intoId: Id.describe('The entity that stays') }).strict(),
  output: KnowledgeEntity,
  successStatus: 200,
  summary: 'Merge two entities of the same kind and space: names, addresses, record, observations, relations and provenance move to the one that stays (CO-1137)',
});

export const createRelation = defineAction({
  name: 'knowledge.relation.create',
  method: 'post',
  path: '/knowledge/relations',
  permission: P.keys.update,
  input: z
    .object({
      fromId: Id,
      toId: Id.optional(),
      toName: z.string().trim().min(1).max(300).optional().describe('A name when the entity is not known yet; resolved when it appears'),
      type: z.string().trim().min(1).max(60).describe('The verb, such as betreft, werkt_aan, nam_deel_aan'),
      sources: graphSources,
    })
    .strict(),
  output: z.object({ id: Id, fromId: Id, toId: Id.nullable(), toName: z.string(), type: z.string() }),
  summary: 'Add a fact as source, relation and target to the graph (CO-1150, CO-1146)',
});

export const createObservation = defineAction({
  name: 'knowledge.observation.create',
  method: 'post',
  path: '/knowledge/observations',
  permission: P.keys.update,
  input: z
    .object({ entityId: Id, category: z.string().trim().min(1).max(60), content: z.string().trim().min(1).max(2000), tags: z.array(z.string().trim().min(1).max(60)).max(20).optional(), sources: graphSources })
    .strict(),
  output: z.object({ id: Id, entityId: Id, category: z.string(), content: z.string(), tags: z.array(z.string()) }),
  summary: 'Add an observation, a fact in a category, to an entity (CO-1150, CO-1146)',
});

const FieldSpec = z.object({ name: z.string(), type: z.enum(['text', 'string', 'date', 'id', 'list', 'number']), description: z.string() });
export const listTypes = defineAction({
  name: 'knowledge.type.list',
  method: 'get',
  path: '/knowledge/types',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({
    itemTypes: z.array(z.object({ type: z.enum(ITEM_TYPES), entityKind: z.enum(ENTITY_KINDS), fields: z.array(FieldSpec), relations: z.array(z.string()), template: z.string() })),
    entityKinds: z.array(z.object({ kind: z.enum(ENTITY_KINDS), app: z.enum(APP_REFS).nullable(), fields: z.array(FieldSpec) })),
  }),
  summary: 'The kinds of knowledge with their typed fields, template and relation types, and the kinds of entities (CO-1145, CO-1128)',
});

export const listDuplicates = defineAction({
  name: 'knowledge.item.duplicates',
  method: 'get',
  path: '/knowledge/duplicates',
  permission: P.keys.read,
  input: z.object({
    spaceId: Id.optional(),
    itemId: Id.optional().describe('Only pairs with this item, whenever it changed'),
    since: z
      .string()
      .regex(/^(\d{1,3}d|\d{4}-\d{2}-\d{2}(T[\d:.]+Z)?)$/, '7d, a date or a moment')
      .optional()
      .describe('Compare the items changed since then with the rest of the space: 7d (default), 30d, a date or a moment'),
    excludeTypes: CsvEnum(ITEM_TYPES).optional().describe('Types left out; default session,handover, empty for none'),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  }),
  output: z.object({ pairs: z.array(z.object({ a: z.object({ id: Id, title: z.string() }), b: z.object({ id: Id, title: z.string() }), similarity: z.number() })) }),
  summary: 'Active items that look alike in title and text, most alike first, to fold together: what changed lately against the rest of the space (CO-1151, #1395)',
});

// ---------- events ----------

export const itemWritten = defineEvent({
  type: 'knowledge.item.written',
  version: 1,
  payload: z.object({
    itemId: Id,
    spaceId: Id,
    change: z.enum(['created', 'updated', 'moved', 'archived', 'restored']),
    byKind: z.enum(AUTHOR_KINDS),
  }),
});

export const documentIndexed = defineEvent({
  type: 'knowledge.document.indexed',
  version: 1,
  payload: z.object({ documentId: Id, spaceId: Id, sourceId: Id }),
});

export const sourceFailed = defineEvent({
  type: 'knowledge.source.failed',
  version: 1,
  payload: z.object({ sourceId: Id, failures: z.number().int(), error: z.string() }),
});

export const itemForgotten = defineEvent({
  type: 'knowledge.item.forgotten',
  version: 1,
  payload: z.object({ itemId: Id, spaceId: Id }),
});

export const knowledgeEvents = [itemWritten, itemForgotten, documentIndexed, sourceFailed];
